
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import JSZip from 'jszip';


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A timestamp fit for a file name: Eastern, 12-hour, never military time and
 * never the viewer's local zone.
 *
 * Exports get mailed and filed, so the time in the name has to mean the same
 * thing to everyone who reads it — an export stamped in the browser's local
 * zone is a different hour on a laptop that travelled.
 *
 * The date leads in `YYYY-MM-DD` so a folder of exports sorts chronologically;
 * the time follows in the form people actually read. The colon becomes a
 * hyphen because macOS and Windows both refuse it in a file name.
 */
export function exportStamp(d: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}-${get('minute')} ${get('dayPeriod')}`;
}

/** Characters a file name cannot survive on macOS, Windows, or in a URL. */
const FILENAME_UNSAFE = /[\\/:*?"<>|#%{}$!'@`= -]/g;

/**
 * What an exported resident record is called on disk.
 *
 * "CARP396 - 1055 SENECA AVE - 2026-09-17 3-42 PM" — the job number first,
 * because that is what these are filed and searched by; then the street, so it
 * can be recognised without opening it; then when it was taken, because the
 * same record gets exported more than once and the old one must not be
 * silently replaced in the Downloads folder.
 *
 * Only the street line is used. The full stored address carries city, state
 * and ZIP, which would push past what a file name can hold and tell the reader
 * nothing they did not already know.
 */
export function residentExportFileName(
  opts: { carpNumber?: string; address?: string; name?: string },
  at: Date = new Date()
): string {
  const clean = (s: string) => s.replace(FILENAME_UNSAFE, ' ').replace(/\s+/g, ' ').trim();
  const street = clean((opts.address ?? '').split(',')[0]).slice(0, 60);
  const carp = clean(opts.carpNumber ?? '');
  // Falls back to the name only when there is no address at all — a record
  // called just "2026-09-17 3-42 PM" would be unidentifiable in a folder.
  const who = street || clean(opts.name ?? '') || 'resident';
  return [carp, who, exportStamp(at)].filter(Boolean).join(' - ');
}

export async function saveBlob(blob: Blob, filename: string) {
  // Web Share API (files) only on mobile — desktop browsers open a system share
  // dialog instead of downloading, which is confusing and feels broken.
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile && typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
    try {
      const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return;
      }
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
      // fall through to anchor download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function downloadCSVRaw(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadCSV(filename: string, rows: (string|null|undefined)[][]) {
  const csvContent = rows.map(r =>
    r.map(cell => {
      if (cell == null) return "";
      const s = String(cell);
      // RFC 4180 quoting
      return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")
  ).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadZip(filename: string, files: {name: string, content: string}[]) {
  const zip = new JSZip();
  files.forEach(file => {
    zip.file(file.name, file.content);
  });
  const content = await zip.generateAsync({ type: "blob" });
  await saveBlob(content, filename);
}
